import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
	Container,
	FormField,
	Header,
	Select,
	SpaceBetween,
  ColumnLayout,
  Input
} from '@cloudscape-design/components';
import { useForm, Controller } from 'react-hook-form';
import { useNewPlatformFormStore } from '../../../../stores/platforms/newPlatformFormStore';
import useApprovedGcpProjects from '../../../../hooks/query/useApprovedGcpProjects';
import usePlatforms from '../../../../hooks/query/usePlatforms';
import api from '../../../../api';
import { gcpProject, gcpProjectIndexItem } from '../../../../types/gcpProject';
import type { Platform } from '../../../../types/platforms';
import {
  normalizePlatformGcpProjectRef,
  resolveGcpProjectUuid
} from '../../../../helper/gcpProjectPlatformSubnetApproval';
import { StepConfig } from '../steps_config';
import CONSTANTS from '../../../../constants';

type GcpClusterDetailsProps = {
  registerValidation?: (validate: () => Promise<boolean>) => void
}

type TagValue = string | {
  label?: string
  value?: string
}

type GcpProjectTagDetails = {
  platformName?: TagValue
  systemAlias?: string
  platformOwner?: TagValue
  dataClassification?: TagValue
  fundingSourceType?: TagValue
  WBSCode?: string
  costCenter?: string
  group?: TagValue
}

type GcpProjectWithTags = gcpProject & {
  tags?: {
    details?: GcpProjectTagDetails
  }
}

const getTagValue = (value?: TagValue) => {
  if (typeof value === 'string') {
    return value
  }

  return value?.label || value?.value || ''
}


const clusterEnvironmentTypes = [
  {label: 'Development', value: 'dev'},
  {label: 'Staging', value: 'staging'},
  {label: 'Production', value: 'prod'}
]
const platformViewerEmailPattern = /^[a-z0-9._%+-]+@globe\.com\.ph$/i

const normalizePlatformViewers = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.map((email) => String(email ?? '').trim())
  }

  if (typeof value === 'string') {
    return value.split(',').map((email) => email.trim())
  }

  return []
}

const validatePlatformViewers = (value: unknown) => {
  const filledViewers = normalizePlatformViewers(value).filter(Boolean)

  if (filledViewers.length === 0) {
    return 'Please add at least one cluster viewer email address.'
  }

  const hasInvalidViewer = filledViewers.some(
    (email) => !platformViewerEmailPattern.test(email)
  )

  if (hasInvalidViewer) {
    return 'Please enter valid email addresses ending with @globe.com.ph.'
  }

  return true
}

const GcpClusterDetails:FC<GcpClusterDetailsProps> = ({ registerValidation }) => {

  const {
    formValues,
    actions: { setFormValues, setFormValidity }
  } = useNewPlatformFormStore();

  const {
    control,
    setValue,
    trigger,
    formState: {isValid}
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: formValues
  })

  const {
    data: gcpProjects,
    isLoading: isGcpProjectsLoading
  } = useApprovedGcpProjects()

  const {
    data: platforms,
    isLoading: isPlatformsLoading
  } = usePlatforms()

  const getGkeGcpProjects = (project?: gcpProjectIndexItem[]) => {
    return (project ?? [])
    .filter((p) => p.isGKE && p.infrastructureType === "GKE")
    .map((p) => ({label: p.gcpProjectName, value: p.gcpProjectID}))
  }

  const selectedGcpProjectID = formValues.gcpProjectID?.value


  const existingClusterRequest = useMemo(() => {
    if (!selectedGcpProjectID || !platforms?.length) {
      return null
    }

    return (platforms as Platform[]).find((platform) => {
      const platformGcpProjectRef = normalizePlatformGcpProjectRef(
        platform.gcpProjectID ?? platform.details?.gcpProjectID
      )
      const platformGcpProjectID =
        resolveGcpProjectUuid(platformGcpProjectRef, gcpProjects ?? []) ||
        platformGcpProjectRef

      return (
        platformGcpProjectID === selectedGcpProjectID &&
        (
          platform.status === 'APPROVED' ||
          platform.status === 'PENDING_APPROVAL'
        )
      )
    }) ?? null
  }, [selectedGcpProjectID, platforms, gcpProjects])

  const hasExistingClusterRequest = Boolean(existingClusterRequest)

  const validateClusterDetails = useCallback(async () => {
    const fieldsValid = await trigger()
    return fieldsValid && !isPlatformsLoading && !hasExistingClusterRequest
  }, [trigger, isPlatformsLoading, hasExistingClusterRequest])

  useEffect(() => {
    console.log('Form validity changed:', isValid)
    setFormValidity(
      isValid &&
      !isPlatformsLoading &&
      !hasExistingClusterRequest
    )
  }, [isValid, isPlatformsLoading, hasExistingClusterRequest, setFormValidity])

  useEffect(() => {
    registerValidation?.(validateClusterDetails)
  }, [registerValidation, validateClusterDetails])

  const [selectedGcpProject, setSelectedGcpProject] = useState<GcpProjectWithTags | null>(null)
  const [selectedTagsDetails, setSelectedTagsDetails] = useState<GcpProjectTagDetails| null>(null)

  const fetchSingleGcpProject = async (gcpProjectsID: string) => {
    const response = await api.gcpProjects.getSinglegcpProject(
      gcpProjectsID,
      true
    )

    const project = response.body[0] as GcpProjectWithTags
    const tagDetails = project.tags?.details || null

    setSelectedGcpProject(project)
    setSelectedTagsDetails(tagDetails)
    populateFields(project, tagDetails)
  }

  const populateFields = (
    project: GcpProjectWithTags | null = selectedGcpProject,
    tagDetails: GcpProjectTagDetails | null = selectedTagsDetails
  ) => {
    const environmentOption = getEnvironmentOption(project?.type)

    if (environmentOption) {
      setValue('environment', environmentOption, {
        shouldDirty: true,
        shouldValidate: true
      })
      setFormValues('environment', environmentOption)
    }

    const detailValues = {
      platformName: toKebabCase(getTagValue(tagDetails?.platformName)),
      systemAlias: tagDetails?.systemAlias || "",
      platformOwner: toKebabCase(getTagValue(tagDetails?.platformOwner)),
      platformOwnerEmail: project?.details?.platformOwnerEmail || '',
      platformViewer: project?.details?.projectViewerDistributionList,
      deployment: getDeploymentOption(environmentOption?.value)
    }

    Object.entries(detailValues).forEach(([fieldName, fieldValue]) => {
      setValue(fieldName, fieldValue, {
        shouldDirty: true,
        shouldValidate: true
      })
      setFormValues(fieldName, fieldValue)
    })

    setFormValues('isClusterDetailsFilled', true)
  }


  const getEnvironmentOption = (type?: string) => {
    const environmentType = type?.toLowerCase()

    const environmentValue =
      environmentType === 'develop' || environmentType === 'development'
        ? 'dev'
        : environmentType === 'staging' || environmentType === 'stg'
          ? 'staging'
          : environmentType === 'production' || environmentType === 'prod'
            ? 'prod'
            : environmentType

    return clusterEnvironmentTypes.find(
      (environment) => environment.value === environmentValue
    ) || null
  }

  const getDeploymentOption = (environment: string | undefined) => {
    if (!environment) {
      return null
    }

    const deploymentValue = `${environment.toLowerCase()}-prep`

    return CONSTANTS.DEPLOYMENT_LIST.find((option) => option.value === deploymentValue) || null
  }

  const toKebabCase = (value = '') =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

  const dropDownFields: Array<StepConfig> = [
    {
      label: "Data Classification",
      fieldName: "dataClassification",
      description: "",
      placeholder: "Select Data Classification",
      options: CONSTANTS.DATA_CLASSIFICATION_LIST,
      type: "text"
    },
    {
      label: "Tier Level",
      fieldName: "tierLevel",
      description: "",
      placeholder: "Select Tier Level",
      options: CONSTANTS.TIER_LEVEL_OPTIONS,
      type: "text"
    }
  ]

  const selectedDetails = [
    {
      label: 'Platform Name',
      value: formValues.platformName
    },
    {
      label: 'Platform Alias',
      value: formValues.systemAlias
    },
    {
      label: 'Cluster Alias',
      value: formValues.systemAlias
    },
    {
      label: 'Cluster Name',
      value: `atlas-isg-${formValues.systemAlias}-${formValues.environment.value || ""}-gke`
    },
    {
      label: 'Environment',
      value: getTagValue(formValues.environment as TagValue)
    },
    {
      label: 'Deployment',
      value: getTagValue(formValues.deployment as TagValue)
    },
    {
      label: 'Cluster Owner',
      value: formValues.platformOwner
    },
    {
      label: 'Cluster Owner Email',
      value: formValues.platformOwnerEmail
    }
  ]

  return (
    <Container>
      <SpaceBetween direction='vertical' size='l'>
        <Controller
          name='gcpProjectID'
          control={control}
          rules={{ required: 'Please select a GCP Project '}}
          render={({field: {onChange, value}, fieldState: { error }}) => (
            <FormField
              label="GCP Project"
              description="Name of the GCP Project where the cluster will be deployed"
              errorText={error?.message}
              stretch>
              <Select
                placeholder='Select GCP Project'
                filteringType='auto'
                filteringPlaceholder='Search GCP projects'
                statusType={isGcpProjectsLoading ? 'loading' : 'finished'}
                options={getGkeGcpProjects(gcpProjects)}
                selectedOption={value ?? null}
                onChange={async ({detail}) => {
                  const selectedOption = detail.selectedOption
                  onChange(selectedOption)
                  setFormValues('gcpProjectID', selectedOption)
                  if (!selectedOption.value) {
                    return
                  }
                  await fetchSingleGcpProject(selectedOption.value)
                }}
              />
            </FormField>
          )}
          />
        {hasExistingClusterRequest && (
          <Alert
            type="error"
            header="GCP project already has a cluster request"
          >
            The selected GCP project already has an{' '}
            <strong>{existingClusterRequest?.status}</strong> cluster request.
            Only rejected requests can be resubmitted for the same GCP project.
          </Alert>
        )}
        {/* <Controller
          name='platformName'
          control={control}
          rules={{
            required: "Please enter a cluster name",
            pattern: {
              value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
              message: "Cluster name must use kebab-case: lowercase letters separated by hyphens."
            }
          }}
          render={
            ({field: {onChange, value}, fieldState: {error}}) => (
              <FormField
                label="Cluster Name"
                description="Enter the GKE cluster name to create. Use kebab-case with lowercase letters and hyphens, for example atlas-orders-dev."
                errorText={error?.message}
                stretch>
                <Input
                  placeholder='cluster-name'
                  value={value ?? ""}
                  onChange={({detail}) => {
                    onChange(detail.value)
                    setFormValues('platformName', detail.value)
                  }}/>
              </FormField>
            )
          }
        />
        <Controller
          name='systemAlias'
          control={control}
          rules={{
            required: "Please enter a cluster alias",
            pattern: {
              value: /^[a-z]{5}$/,
              message: "Cluster alias must be exactly five lowercase letters."
            }
          }}
          render={
            ({field: {onChange, value}, fieldState: {error}}) => (
              <FormField
                label="Cluster Alias"
                description="Enter a short cluster alias used for naming and identification. Use exactly five lowercase letters, for example atlas."
                errorText={error?.message}
                stretch>
                <Input
                  placeholder='atlas'
                  value={value ?? ""}
                  onChange={({detail}) => {
                    onChange(detail.value)
                    setFormValues('systemAlias', detail.value)
                  }}/>
              </FormField>
            )
          }
        /> */}
        <Controller
            name='platformViewer'
            control={control}
            rules={{ validate: validatePlatformViewers }}
            render={({
              field: { onChange, value },
              fieldState: { error },
            }) => {
              const platformViewers = normalizePlatformViewers(value)
              const viewerInputs = platformViewers.length > 0
                ? platformViewers
                : ['']

              return (
                <FormField
                  label="Cluster Viewer(s)"
                  description="Email addresses for viewer-level permissions on the project. Add one email address per row."
                  errorText={error?.message}
                  stretch
                  >
                    <SpaceBetween direction="vertical" size="xs">
                      {viewerInputs.map((email, index) => (
                        <div
                          key={index}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                          <div style={{ flex: 1 }}>
                            <Input
                              name={`platformViewer-${index}`}
                              placeholder="juan.cruz@globe.com.ph"
                              value={email}
                              onChange={({detail}) => {
                                const list = normalizePlatformViewers(value)
                                const next = [...list]
                                next[index] = detail.value
                                onChange(next)
                                setFormValues(
                                  "platformViewer",
                                  next
                                )
                              }}
                            />
                          </div>
                          <Button
                            variant="link"
                            iconName="remove"
                            ariaLabel={`Remove cluster viewer ${index + 1}`}
                            onClick={() => {
                              const next = normalizePlatformViewers(value).filter(
                                (_, viewerIndex) => viewerIndex !== index
                              )
                              onChange(next)
                              setFormValues(
                                "platformViewer",
                                next
                              )
                            }}
                          />
                        </div>
                      ))}
                      <Button
                        iconName="add-plus"
                        onClick={() => {
                          const list = normalizePlatformViewers(value)
                          const next = list.length > 0 ? [...list, ''] : ['', '']
                          onChange(next)
                          setFormValues(
                            "platformViewer",
                            next
                          )
                        }}
                      >
                        Add
                      </Button>
                    </SpaceBetween>
                </FormField>
              )
            }}
            />
            <ColumnLayout columns={2} variant='default'>
              {dropDownFields.map((field: StepConfig) => {
                return(
                  <Controller
                    name={field.fieldName}
                    control={control}
                    key={field.fieldName}
                    rules={{required: `Please select ${field.label}`}}
                    render={({
                      field: {onChange, value},
                      fieldState: {error}
                    }) => (
                      <FormField
                        label={field.label}
                        description={field.description}
                        errorText={error?.message}
                        stretch
                      >
                        <Select
                          placeholder={field.placeholder}
                          options={field.options}
                          selectedOption={value ?? null}
                          onChange={({detail}) => {
                            const selectedOption = detail.selectedOption
                            onChange(selectedOption)
                            setFormValues(field.fieldName, selectedOption)
                          }}
                        />
                        
                      </FormField>
                    )}
                  />
                )
              })}
            </ColumnLayout>
            <Container header={<Header variant='h3'>Cluster Details</Header>}>
              <ColumnLayout columns={2} variant='text-grid'>
                {selectedDetails.map((detail) => (
                  <div key={detail.label}>
                    <Box variant='awsui-key-label'>{detail.label}</Box>
                    <div>{(detail.value) || '-'}</div>
                  </div>
                ))}
              </ColumnLayout>
            </Container>
      </SpaceBetween>
    </Container>
  );
} 

export default GcpClusterDetails
